class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = this.querySelector('#cart-notification');
    this.overlay = this.querySelector('.avp-cart-overlay');
    this.itemsContainer = this.querySelector('#cart-notification-product');
    this.totalElement = this.querySelector('#avp-cart-total');
    this.progressMessage = this.querySelector('#avp-shipping-progress-message');
    this.progressBar = this.querySelector('#avp-shipping-progress-bar');
    this.progressTrack = this.querySelector('.avp-shipping-progress__track');
    this.reassuranceElement = this.querySelector('.avp-cart-reassurance');
    this.currency = this.dataset.currency || 'EUR';
    this.onBodyClick = this.handleBodyClick.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.notification.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.itemsContainer.addEventListener('click', (event) => this.handleCartAction(event));

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;
      event.preventDefault();
      this.setActiveElement(cartLink);
      this.open();
    });
  }

  async open() {
    await this.refreshCart();
    this.classList.add('active');
    document.body.classList.add('avp-cart-open');
    requestAnimationFrame(() => this.notification.focus());
    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('avp-cart-open');
    document.body.removeEventListener('click', this.onBodyClick);
    if (this.activeElement) this.activeElement.focus();
  }

  renderContents(parsedState) {
    if (parsedState && parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const parsed = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
      const replacement = parsed.querySelector('#cart-icon-bubble');
      if (bubble && replacement) bubble.innerHTML = replacement.innerHTML;
    }
    this.open();
  }

  getSectionsToRender() {
    return [{ id: 'cart-icon-bubble' }];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : html;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handleBodyClick(event) {
    if (!this.classList.contains('active')) return;
    if (event.target.closest('#cart-notification') || event.target.closest('#cart-icon-bubble')) return;
    this.close();
  }

  async handleCartAction(event) {
    const button = event.target.closest('[data-cart-action]');
    if (!button || button.disabled) return;
    const line = Number(button.dataset.line);
    const currentQuantity = Number(button.dataset.quantity || 0);
    const maximumQuantity = Number(button.dataset.maxQuantity || 0);
    let quantity = currentQuantity;
    if (button.dataset.cartAction === 'increase') {
      if (maximumQuantity > 0 && currentQuantity >= maximumQuantity) return;
      quantity = maximumQuantity > 0 ? Math.min(maximumQuantity, currentQuantity + 1) : currentQuantity + 1;
    }
    if (button.dataset.cartAction === 'decrease') quantity = Math.max(0, quantity - 1);
    if (button.dataset.cartAction === 'remove') quantity = 0;
    button.disabled = true;
    try {
      await this.changeLine(line, quantity);
    } finally {
      if (button.isConnected && button.dataset.locked !== 'true') button.disabled = false;
    }
  }

  async changeLine(line, quantity) {
    try {
      const response = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok) {
        this.showCartError(this.getCartErrorMessage(result));
        return;
      }

      await this.refreshCart();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      this.showCartError('Der Warenkorb konnte gerade nicht aktualisiert werden. Bitte versuche es erneut.');
    }
  }

  getCartErrorMessage(result) {
    if (!result) return 'Der Warenkorb konnte gerade nicht aktualisiert werden. Bitte versuche es erneut.';

    const error = result.description || result.errors || result.message;
    if (typeof error === 'string' && error.trim()) return error;
    if (Array.isArray(error)) return error.filter(Boolean).join(' ');
    if (error && typeof error === 'object') return Object.values(error).flat().filter(Boolean).join(' ');

    return 'Die gewünschte Menge ist derzeit nicht verfügbar.';
  }

  showCartError(message) {
    let error = this.itemsContainer.querySelector('.avp-cart-error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'avp-cart-error';
      error.setAttribute('role', 'alert');
      this.itemsContainer.prepend(error);
    }
    error.textContent = message;
  }

  async refreshCart() {
    this.itemsContainer.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart fetch failed');
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      this.showCartError('Der Warenkorb konnte gerade nicht geladen werden.');
    } finally {
      this.itemsContainer.removeAttribute('aria-busy');
    }
  }

  renderCart(cart) {
    this.currentCart = cart;
    if (!cart.items.length) {
      this.itemsContainer.innerHTML = '<div class="avp-cart-empty"><p>Dein Warenkorb ist noch leer.</p><a class="button button--secondary" href="/collections/all">Weiter shoppen</a></div>';
    } else {
      this.itemsContainer.innerHTML = cart.items.map((item, index) => this.itemMarkup(item, index + 1)).join('');
    }
    if (this.totalElement) this.totalElement.textContent = this.formatMoney(cart.total_price);
    this.updateShippingProgress(cart);
    this.updateReassurance(cart);
    this.updateCartBubble(cart.item_count);
    const recommendations = this.querySelector('avp-cart-recommendations');
    if (recommendations && typeof recommendations.renderForCart === 'function') {
      recommendations.renderForCart(cart);
    }
  }

  itemMarkup(item, line) {
    const image = item.image ? `<img src="${this.escapeHtml(item.image)}&width=180" alt="${this.escapeHtml(item.product_title)}" loading="lazy" width="90" height="90">` : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="avp-cart-item__variant">${this.escapeHtml(item.variant_title)}</div>` : '';
    const properties = Object.entries(item.properties || {}).filter(([, value]) => value).map(([key, value]) => `<div class="avp-cart-item__property"><span>${this.escapeHtml(key)}:</span> ${this.escapeHtml(String(value))}</div>`).join('');
    const isKindergartenSet = item.product_type === 'Kindergartenstart-Set';
    const increaseAttributes = isKindergartenSet ? ' disabled aria-disabled="true" data-locked="true" data-max-quantity="1"' : '';
    return `<article class="avp-cart-item">
      <a class="avp-cart-item__image" href="${this.escapeHtml(item.url)}">${image}</a>
      <div class="avp-cart-item__details">
        <a class="avp-cart-item__title" href="${this.escapeHtml(item.url)}">${this.escapeHtml(item.product_title)}</a>
        ${variant}${properties}
        <div class="avp-cart-item__bottom">
          <div class="avp-cart-quantity" aria-label="Menge">
            <button type="button" data-cart-action="decrease" data-line="${line}" data-quantity="${item.quantity}" aria-label="Menge verringern">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-action="increase" data-line="${line}" data-quantity="${item.quantity}" aria-label="Menge erhöhen"${increaseAttributes}>+</button>
          </div>
          <strong>${this.formatMoney(item.final_line_price)}</strong>
        </div>
        <button type="button" class="avp-cart-remove" data-cart-action="remove" data-line="${line}" data-quantity="${item.quantity}">Entfernen</button>
      </div>
    </article>`;
  }

  updateShippingProgress(cart) {
    if (!this.progressMessage || !this.progressBar || !this.progressTrack) return;
    const threshold = 7400;

    if (cart.total_price >= threshold) {
      this.progressMessage.textContent = 'Geschafft: Dein Versand innerhalb Deutschlands ist kostenlos.';
      this.progressBar.style.width = '100%';
      return;
    }

    const amountLeft = Math.max(0, threshold - cart.total_price);
    const progress = Math.min(100, Math.round((cart.total_price / threshold) * 100));
    this.progressMessage.textContent = 'Noch ' + this.formatMoney(amountLeft) + ' bis zum kostenlosen Versand in Deutschland.';
    this.progressBar.style.width = progress + '%';
  }

  updateReassurance(cart) {
    if (!this.reassuranceElement) return;
    const handmadeOnly =
      cart.items.length > 0 &&
      cart.items.every((item) => String(item.vendor || '').toLowerCase() === 'avocadopink');
    this.reassuranceElement.textContent = handmadeOnly
      ? 'Handgefertigt in Roth · Sicher bezahlen · Persönlich für dich'
      : 'Sorgfältig ausgewählt · Sicher bezahlen · Persönlicher Service';
  }

  updateCartBubble(count) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    let countBubble = bubble.querySelector('.cart-count-bubble');
    if (count === 0) {
      if (countBubble) countBubble.remove();
      return;
    }
    if (!countBubble) {
      countBubble = document.createElement('div');
      countBubble.className = 'cart-count-bubble';
      countBubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      bubble.appendChild(countBubble);
    }
    let visibleCount = countBubble.querySelector('span[aria-hidden="true"]');
    if (!visibleCount) {
      visibleCount = document.createElement('span');
      visibleCount.setAttribute('aria-hidden', 'true');
      countBubble.prepend(visibleCount);
    }
    let accessibleCount = countBubble.querySelector('.visually-hidden');
    if (!accessibleCount) {
      accessibleCount = document.createElement('span');
      accessibleCount.className = 'visually-hidden';
      countBubble.appendChild(accessibleCount);
    }
    visibleCount.textContent = count < 100 ? count : '';
    accessibleCount.textContent = count === 1 ? '1 Artikel' : `${count} Artikel`;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: this.currency }).format(cents / 100);
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }
}

customElements.define('cart-notification', CartNotification);
